import { useQuery } from "react-query";
import { fetchStore } from "../../services";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { BasicTable, Loader, Button, ConfirmDialog, Notification, ReplaceBillingConfigModal } from "../../components";
import { useContext, useEffect, useRef, useState } from 'react';
import { UserContext } from '../../hooks/UserContext';
import { Link, useHistory } from "react-router-dom/cjs/react-router-dom.min";
import { disableBillingAtCutoff, fetchStoreBillingConfigurations, fetchStoreBillingStatements, deleteStoreBillingConfiguration } from "../../services/storeService"
import { Pagination } from 'antd'
import { BILLING_STORE_CONFIG_CREATE, BILLING_STORE_CONFIG_DUPLICATE, BILLING_STORE_CREATE_CUSTOM_SERVICE, STORE_BILLING_STATUS_ACTIVE } from "../../navigation/constants"
import ListCustomServicesContainer from "./ListCustomServicesContainer"
import { isSuperAdminUser } from "../../utils/userUtils";
import { BillingConfigurationHistoryModal } from "./BillingConfigurationHistoryModal";
import { ClockIcon } from '@heroicons/react/outline';
import { formatShortDate, formatCurrency, formatMonthYear, getStatementStatusClasses } from '../../utils/billingUtils';

export const ShowStoreBillingConfigsContainer = () => {

    const { store_id } = useParams()
    const {warehouse_id} = useParams()
    const { t, i18n} = useTranslation()
    const {user} = useContext(UserContext)
    const history = useHistory()
    const isSuperAdmin = isSuperAdminUser(user)
    const [currentWarehouse, setCurrentWarehouse] = useState(null)
    const [billingConfigurations, setBillingConfigurations] = useState([])
    const [showConfirmDialog, setShowConfirmDialog] = useState(false)
    const [notificationMessage, setNotificationMessage] = useState({show: false, title: "", type: ""})
    const [busy, setBusy] = useState(false)
    const [showReplaceModal, setShowReplaceModal] = useState(false)
    const [showHistoryModal, setShowHistoryModal] = useState(false)
    const [historyStatementId, setHistoryStatementId] = useState(null)
    const [statementsPage, setStatementsPage] = useState(1)
    const [showDeleteConfirmDialog, setShowDeleteConfirmDialog] = useState(false)
    const [configToDelete, setConfigToDelete] = useState(null)
    const [busyDeleting, setBusyDeleting] = useState(false)
    const tableRef = useRef()

    // Validate warehouse access
    useEffect(() => {
        if (user && warehouse_id) {
            const hasAccess = isSuperAdmin || 
                            user.read_all_warehouses || 
                            user.warehouse_ids?.includes(parseInt(warehouse_id))
            
            if (!hasAccess) {
                // User doesn't have access to this warehouse, redirect
                history.replace('/')
            }
        }
    }, [user, warehouse_id, isSuperAdmin, history])
    const {
        isLoading,
        isFetching,
        isError,
        error,
        data,
        isPreviousData,
        refetch
    } = useQuery(['store', store_id], ()=>fetchStore(store_id), {
        keepPreviousData: true,
        refetchOnWindowFocus: false
    })
    const {
        isLoading: isLoadingBillingConfigurations,
        isFetching: isFetchingBillingConfigurations,
        isError: isErrorBillingConfigurations,
        error: errorBillingConfigurations,
        data: dataBillingConfigurations,
        isPreviousData: isPreviousDataBillingConfigurations,
        refetch: refetchBillingConfigurations
    } = useQuery(['store_billing_configurations', store_id], ()=>fetchStoreBillingConfigurations({store_id: store_id}), {
        keepPreviousData: true,
        refetchOnWindowFocus: false
    })

    const {
        data: statementsData,
        isLoading: isLoadingStatements,
        isFetching: isFetchingStatements,
    } = useQuery(
        ['store_billing_statements', store_id, statementsPage],
        () => fetchStoreBillingStatements({ store_id: store_id, page: statementsPage, per_page: 10 }),
        { keepPreviousData: true, refetchOnWindowFocus: false }
    )

    const statementsList = statementsData?.store_billing_statements || []
    const statementsMeta = statementsData?.meta || {}
    const storeCurrency = currentWarehouse?.country === 'BR' ? 'BRL' : 'MXN'

    const getCurrentWarehouse = () => {
        const warehouse = data?.warehouses.find(warehouse => warehouse.id == warehouse_id)
        setCurrentWarehouse(warehouse)
    }

    useEffect(() => {
        getCurrentWarehouse()
    }, [data])

    useEffect(() => {
        dataBillingConfigurations && setBillingConfigurations(dataBillingConfigurations.store_billing_configurations)
    }, [dataBillingConfigurations])
    
    const navigate = (billing_configuration_id) => {
        history.push(BILLING_STORE_CONFIG_DUPLICATE.replace(':warehouse_id', warehouse_id).replace(':store_id', store_id).replace(':billing_configuration_id', billing_configuration_id))
        
    }

    const columns = [
        {
            accessor: 'id',
            Header: "ID",
            disableFilters: false,
            disableSortBy: false,
            searchAs: 'name'
        },
        {
            accessor: 'status',
            Header: t('generic.status'),
            disableFilters: true,
            disableSortBy: true,
            shrinkToContent: true
        },
        {
            accessor: 'from',
            Header: t('generic.from'),
            disableFilters: true,
            disableSortBy: true,
        },
        {
            accessor: 'to',
            Header: t('generic.to'),
            disableFilters: false,
            disableSortBy: true,
            searchAs: 'account_name'
        },
        {
            accessor: 'statement_count',
            Header: t('generic.billing_statements'),
            disableFilters: true,
            disableSortBy: false
        },
        {
            accessor: 'actions',
            Header: '',
            fetchingIndicator : true,
            disableFilters: true,
            disableSortBy: true,
            shrinkToContent: true
        },
    ]

    const formatDate = (date) => formatShortDate(date, t('generic.undefined'))

    const openStatementHistory = (statementId) => {
        setHistoryStatementId(statementId)
        setShowHistoryModal(true)
    }

    const isFutureConfig = (billing_configuration) => {
        return new Date(billing_configuration.valid_from) > new Date()
    }

    const handleDeleteConfig = async (configId) => {
        setBusyDeleting(true)
        try {
            await deleteStoreBillingConfiguration(configId)
            setNotificationMessage({show: true, type: "success", title: t("billing.delete_config.success_message")})
            refetchBillingConfigurations()
        } catch (error) {
            const errorMsg = error?.response?.data?.error || error?.response?.data?.message || ''
            let translatedError = t("billing.delete_config.error_generic")
            if (errorMsg.includes('usages associated to statements')) {
                translatedError = t("billing.delete_config.error_has_usages")
            } else if (error?.response?.status === 409) {
                if (errorMsg.includes('replacement is in progress')) {
                    translatedError = t("billing.delete_config.error_replacement_in_progress")
                } else {
                    translatedError = t("billing.delete_config.error_operation_in_progress")
                }
            }
            setNotificationMessage({show: true, type: "error", title: translatedError})
        } finally {
            setBusyDeleting(false)
            setShowDeleteConfirmDialog(false)
            setConfigToDelete(null)
        }
    }

    const isFirstDayOfMonth = () => {
        const today = new Date()
        return today.getDate() === 1
    }

    const hasFutureBillingConfiguration = () => {
        return billingConfigurations.some(billing_configuration => {
            const dateNow = new Date()
            const date_to = billing_configuration.valid_to
            const date_from = billing_configuration.valid_from
            return new Date(date_from) > dateNow
        })
    }

    const getFutureBillingConfigurations = () => {
        return billingConfigurations.filter(billing_configuration => {
            const dateNow = new Date()
            const date_from = billing_configuration.valid_from
            return new Date(date_from) > dateNow
        })
    }

    const handleReplaceSuccess = () => {
        setNotificationMessage({show: true, type: "success", title: t("billing.replace_config.success_message")})
        refetchBillingConfigurations()
    }

    const handleReplaceError = (error) => {
        setNotificationMessage({show: true, type: "error", title: error})
    }

    const hasCurrentBillingConfiguration = () => {
        return currentBillingConfiguration ? true : false
    }

    const canBillingBeDisabled = () => {
        if(!hasCurrentBillingConfiguration())
            return false
        else if(hasFutureBillingConfiguration())
            return true
        else return currentBillingConfiguration.valid_to ? false : true
        
    }

    const getStatusCapsule = (billing_configuration) => {
        const dateNow = new Date()
        const date_to = billing_configuration.valid_to
        const date_from = billing_configuration.valid_from
        let status = ""
        let color = ""
        if(!date_to) {
            if (new Date(date_from) > dateNow) {
                status = "future"
                color = "blue"
            }
            else {
                status = "ongoing"
                color = "green"
            }
        }
        else if (new Date(date_to) > dateNow && new Date(date_from) < dateNow) {
            status = "ongoing"
            color = "green"
        }
        else if (new Date(date_to) < dateNow) {
            status = "billed"
            color = "gray"
        }
        else {
            status = "future"
            color = "blue"
        }
        return (
            <span className={`px-2 inline-flex text-xs leading-5 rounded-full bg-${color}-200 text-${color}-500`}>
                {t(`billing.${status}`)}
            </span>
        )
    }


    const getTableData = () => {
        return billingConfigurations.map(billing_configuration => {
            return {
                ...billing_configuration,
                status: getStatusCapsule(billing_configuration),
                from: formatDate(billing_configuration.valid_from),
                to: formatDate(billing_configuration.valid_to),
                actions: (
                    <div className="flex space-x-2 items-center">
                        <Link to={BILLING_STORE_CONFIG_DUPLICATE.replace(':warehouse_id', warehouse_id).replace(':store_id', store_id).replace(':billing_configuration_id', billing_configuration.id)} className="text-black hover:text-black">
                            <button className="text-left whitespace-nowrap border border-gray-300 shadow-sm rounded-lg py-2 px-3 text-sm text-gray-700 font-medium hover:bg-gray-50 outline-none">
                                {t('billing.duplicate')}
                            </button>
                        </Link>
                        {isFutureConfig(billing_configuration) && (
                            <button
                                className="text-left whitespace-nowrap border border-red-300 shadow-sm rounded-lg py-2 px-3 text-sm text-red-600 font-medium hover:bg-red-50 outline-none"
                                onClick={() => {
                                    setConfigToDelete(billing_configuration.id)
                                    setShowDeleteConfirmDialog(true)
                                }}
                            >
                                {t('billing.delete_config.button')}
                            </button>
                        )}
                    </div>
                ),
            }
        }) 
    }

    const disableBillingForStore = async (store_id) => {
        setBusy(true)
        try {
            await disableBillingAtCutoff(store_id)
            setNotificationMessage({show: true, type: "success", title: i18n.t("billing.disable_billing_for_store_success")})
        } catch (error) {
            alert(error)
            console.log(error)
        } finally {
            setBusy(false)
            setShowConfirmDialog(false)
            refetchBillingConfigurations()
        }
    }

    const buildCreateConfigLink = () => {
        let pageLink = null

        if(billingConfigurations?.length > 0){
            pageLink = BILLING_STORE_CONFIG_DUPLICATE.replace(':warehouse_id', warehouse_id).replace(':store_id', store_id).replace(':billing_configuration_id', billingConfigurations[0].id)
        } else{
            pageLink = BILLING_STORE_CONFIG_CREATE.replace(':warehouse_id', warehouse_id).replace(':store_id', store_id)
        }

        return pageLink
    }

    const currentBillingConfiguration = billingConfigurations.find((billingConfiguration) => {
        const dateNow = new Date()
        const dateTo = billingConfiguration.valid_to
        const dateFrom = billingConfiguration.valid_from

        const isCurrentConfig = (new Date(dateFrom)) <= dateNow && (!dateTo || (new Date(dateTo)) >= dateNow) ? true : false
        return isCurrentConfig
    })

    return (
        <>
            {isLoading || isLoadingBillingConfigurations ? (
                <><Loader show={true}></Loader></>
            ) : isError ? (
                <>Error: {error.message}</>
            ) : (
                <div className="pb-6">
                    <div className="flex flex-col rounded-lg border border-gray-300">
                        <div className="flex border-b border-gray-300 p-4">
                            <div className="w-1/3 font-semibold">{t('generic.store')}</div>
                            <>{data.name}</>
                        </div>
                        <div className="flex border-b border-gray-300 p-4">
                            <div className="w-1/3 font-semibold">{t('generic.country')}</div>
                            <>{currentWarehouse?.country == "BR" ? "Brasil" : "México"}</>
                        </div>
                        <div className="flex p-4">
                            <div className="w-1/3 font-semibold">{t('generic.warehouse')}</div>
                            <>{currentWarehouse?.name}</>
                        </div>
                    </div>

                    <div className="mt-6 mb-2">
                        <div className="flex justify-between items-center">
                            <h2 className="font-bold text-lg my-6 mb-4">{t('billing.billing_custom_services')}</h2>
                        </div>


                        <ListCustomServicesContainer />
                    </div>

                    <div className="flex justify-between items-center mt-6 mb-2">
                        <h2 className="my-6 mb-4 font-bold text-lg">{t('billing.billing_configurations_history')}</h2>
                        <div className="flex space-x-2">
                            <button
                                className={`text-left whitespace-nowrap border border-gray-300 shadow-sm rounded-lg py-2 px-3 text-sm font-medium outline-none ${
                                    isLoadingBillingConfigurations || !hasFutureBillingConfiguration() || isFirstDayOfMonth()
                                        ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                                        : 'bg-white text-gray-700 hover:bg-gray-50'
                                }`}
                                onClick={() => setShowReplaceModal(true)}
                                disabled={isLoadingBillingConfigurations || !hasFutureBillingConfiguration() || isFirstDayOfMonth()}
                            >
                                {t('billing.replace_config.title')}
                            </button>
                            <Link to={buildCreateConfigLink} className="text-black hover:text-black">
                                <button className="bg-blue-500 hover:bg-blue-600 rounded-lg px-4 py-2 text-sm font-medium text-white">
                                    {t('billing.create')}
                                </button>
                            </Link>
                        </div>
                    </div>
                    <BasicTable
                        columns={columns}
                        showHeader
                        selectable = {false}
                        showCount = {true}
                        showLoader={isFetching}
                        data = {getTableData()}
                        emptyTableText={t('generic.empty_table')}
                        ref = {tableRef}
                    />

                    {/* Statements section */}
                    <div className="mt-6 mb-2 relative">
                        <h2 className="my-6 mb-4 font-bold text-lg">{t('billing.statements_section_title')}</h2>
                        {isLoadingStatements ? (
                            <div className="flex justify-center py-8">
                                <Loader show={true} />
                            </div>
                        ) : statementsList.length === 0 ? (
                            <p className="text-gray-500 text-sm py-4 text-center">{t('billing.statements_empty')}</p>
                        ) : (
                            <>
                                {isFetchingStatements && <div className="absolute inset-0 bg-white bg-opacity-50 z-10" />}
                                <div className="space-y-2">
                                    {statementsList.map(statement => {
                                        const statusClasses = getStatementStatusClasses(statement.status)
                                        const hasFinancialData = statement.total_without_tax != null
                                        const total = parseFloat(statement.total_without_tax || 0)
                                        const tax = parseFloat(statement.total_tax || 0)
                                        const credits = parseFloat(statement.billed_credit || 0)

                                        return (
                                            <div
                                                key={statement.id}
                                                className="border border-gray-200 rounded-lg p-3 hover:border-gray-300 transition-colors"
                                            >
                                                <div className="flex items-start justify-between">
                                                    <div className="flex-1 min-w-0">
                                                        <div className="flex items-center gap-2">
                                                            <p className="text-sm font-semibold text-gray-900">
                                                                {formatMonthYear(statement.cutoff_at)}
                                                            </p>
                                                            {statement.status && (
                                                                <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${statusClasses}`}>
                                                                    {t(`billing.recalculate_statement.statement_status_${statement.status}`, statement.status)}
                                                                </span>
                                                            )}
                                                        </div>
                                                        <p className="text-xs text-gray-500 mt-0.5">
                                                            {t('billing.recalculate_statement.statement_cutoff')}: {formatDate(statement.cutoff_at)}
                                                        </p>
                                                        {hasFinancialData && (
                                                            <div className="flex flex-wrap gap-x-4 gap-y-0.5 mt-1.5 text-xs">
                                                                <span className="text-gray-700">
                                                                    <span className="text-gray-500">{t('billing.recalculate_statement.statement_total')}:</span>{' '}
                                                                    <span className="font-medium">{formatCurrency(total, storeCurrency)}</span>
                                                                </span>
                                                                {tax > 0 && (
                                                                    <span className="text-gray-700">
                                                                        <span className="text-gray-500">{t('billing.recalculate_statement.statement_tax')}:</span>{' '}
                                                                        <span className="font-medium">{formatCurrency(tax, storeCurrency)}</span>
                                                                    </span>
                                                                )}
                                                                {credits > 0 && (
                                                                    <span className="text-green-700">
                                                                        <span className="text-green-600">{t('billing.recalculate_statement.statement_credits')}:</span>{' '}
                                                                        <span className="font-medium">-{formatCurrency(credits, storeCurrency)}</span>
                                                                    </span>
                                                                )}
                                                            </div>
                                                        )}
                                                        {statement.pay_before && (
                                                            <p className="text-xs text-gray-500 mt-1">
                                                                {t('billing.recalculate_statement.statement_pay_before')}: {formatDate(statement.pay_before)}
                                                            </p>
                                                        )}
                                                    </div>
                                                    <button
                                                        className="flex items-center gap-1 whitespace-nowrap border border-gray-300 shadow-sm rounded-lg py-2 px-3 text-sm text-gray-700 font-medium hover:bg-gray-50 outline-none"
                                                        onClick={() => openStatementHistory(statement.id)}
                                                    >
                                                        <ClockIcon className="h-4 w-4" />
                                                        {t('billing.view_statement_history')}
                                                    </button>
                                                </div>
                                            </div>
                                        )
                                    })}
                                </div>
                                {statementsMeta.total_pages > 1 && (
                                    <div className="flex justify-end mt-4">
                                        <Pagination
                                            size="small"
                                            current={statementsMeta.current_page}
                                            total={statementsMeta.total_count}
                                            pageSize={10}
                                            onChange={(p) => setStatementsPage(p)}
                                            showSizeChanger={false}
                                        />
                                    </div>
                                )}
                            </>
                        )}
                    </div>

                    <div className="flex flex-col mt-6 mb-2">
                        <h2 className="my-6 mb-4 font-bold text-lg">{t('billing.disable_title')}</h2>
                        <div>
                            <Button type="danger" onClick={() => setShowConfirmDialog(true)} disabled={!canBillingBeDisabled()}>
                                {canBillingBeDisabled() ? t('billing.disable') : t('billing.already_disabled')}
                            </Button>
                        </div>
                    </div>
                    <ConfirmDialog
                        open={showConfirmDialog}
                        setOpen={(val) => setShowConfirmDialog(val)}
                        cancelLabel= {i18n.t("billing.confirm_disable_dialog.cancel")}
                        confirmLabel= {i18n.t("billing.confirm_disable_dialog.confirm")}
                        description={` ${i18n.t("billing.confirm_disable_dialog.description")}`}
                        title= {i18n.t("billing.confirm_disable_dialog.title")} 
                        loading={busy}
                        onConfirm={() => disableBillingForStore(data.id)}
                    />
                    <ConfirmDialog
                        open={showDeleteConfirmDialog}
                        setOpen={(val) => {
                            if (busyDeleting) return
                            setShowDeleteConfirmDialog(val)
                            if (!val) setConfigToDelete(null)
                        }}
                        cancelLabel={t("billing.confirm_delete_dialog.cancel")}
                        confirmLabel={t("billing.confirm_delete_dialog.confirm")}
                        description={t("billing.confirm_delete_dialog.description")}
                        title={t("billing.confirm_delete_dialog.title")}
                        loading={busyDeleting}
                        onConfirm={() => handleDeleteConfig(configToDelete)}
                    />
                    <Notification
                        show={notificationMessage.show}
                        title={notificationMessage.title}
                        type={notificationMessage.type}
                        setShow={showNotification => setNotificationMessage({...notificationMessage, show: showNotification})}
                    />
                    
                    <ReplaceBillingConfigModal
                        open={showReplaceModal}
                        setOpen={setShowReplaceModal}
                        storeId={store_id}
                        futureConfigurations={getFutureBillingConfigurations()}
                        onSuccess={handleReplaceSuccess}
                        onError={handleReplaceError}
                    />
                    <BillingConfigurationHistoryModal
                        open={showHistoryModal}
                        setOpen={setShowHistoryModal}
                        storeId={store_id}
                        statementId={historyStatementId}
                        currency={storeCurrency}
                    />
                </div>
            )}
        </>
    )
};
